import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Box, Button, Heading, Text } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { colors, spaces, typography } from '@99designs/emu/settings';
import { FormattedMessage } from '@99designs/frontend-components/i18n';
import { FadeTransition } from '../../components/FadeTransition';
import { useOnboarderNav } from '../../components/Onboarder/useOnboarderNav';
import { useRouteTransition } from '../../components/RouteTransitionProvider';
import { ReactComponent as Wanda } from '../images/wand-lady-and-browser.svg';

const Container = styled(FadeTransition)`
    display: flex;
    align-items: center;
    align-self: center;
    flex-grow: 1;
    width: 100%;
    max-width: 1300px;
    padding-left: ${spaces['1.5bu']};

    ${mediaQuery.between('small', 'medium')} {
        flex-direction: column;
        justify-content: flex-start;
        padding: 0 ${spaces['1.5bu']};
    }
`;

const HeadingWrapper = styled.div`
    max-width: 70%;
    padding-right: ${spaces['3bu']};

    ${mediaQuery.between('small', 'medium')} {
        max-width: 100%;
        padding: 0;
        padding-top: 10%;
    }
    ${mediaQuery.only('medium')} {
        padding-top: 120px;
    }
`;

const StyledHeading = styled(Heading)`
    font-size: ${typography.typeScale.size9}px;

    ${mediaQuery.only('medium')} {
        font-size: ${typography.typeScale.size6}px;
    }
    ${mediaQuery.only('small')} {
        font-size: ${typography.typeScale.size5}px;
    }
`;

const HeroContainer = styled.div`
    position: relative;
    width: 100%;
    max-width: 565px;
    padding-right: ${spaces['0.75bu']};

    ${mediaQuery.between('small', 'medium')} {
        position: absolute;
        max-width: 450px;
        width: 70%;
        right: 0%;
    }

    ${mediaQuery.only('small')} {
        top: 50%;
    }

    ${mediaQuery.only('medium')} {
        top: 35%;
    }

    ${mediaQuery.only('large')} {
        margin-right: -5%;
    }

    &:after {
        content: '';
        position: fixed;
        background-color: #81a3e6;
        right: -26%;
        top: -20%;
        width: 40%;
        height: 200%;
        transform: rotate(195deg);
        z-index: -1;

        ${mediaQuery.between('small', 'medium')} {
            transform: rotate(-30deg);
            left: -40%;
            top: 73%;
            width: 250%;
            height: 100%;
        }
    }
`;

const WandaIcon = styled(Wanda)`
    display: inline-block;
    width: inherit;
    height: inherit;
`;

const WandaIconWrapper = styled.div`
    width: 100%;
    max-width: 565px;
    ${mediaQuery.between('small', 'medium')} {
        max-width: 450px;
    }

    ${mediaQuery.only('medium')} {
        padding-top: ${spaces['6bu']};
    }
`;

const AttributionWrapper = styled.div`
    position: absolute;
    right: 59%;
    bottom: 57%;
`;

const Attribution = styled.p`
    font-family: ${typography.fontFamilies.attribution};
    color: ${colors.grey700};
    font-style: italic;
    font-size: ${typography.typeScale.size2}px;
    line-height: ${typography.lineHeights.medium};
    margin-bottom: 0;
`;

export function WebOnboarderLauncher() {
    const onboarderNav = useOnboarderNav();
    const { transitionState, enableTransition } = useRouteTransition();
    useEffect(() => {
        return enableTransition(500);
    });

    return (
        <Container state={transitionState}>
            <HeadingWrapper>
                <Box marginBottom="1.5bu">
                    <StyledHeading accessibilityLevel={1}>
                        {__('Let’s find the right solution for your website needs.')}
                    </StyledHeading>
                </Box>
                <Box marginBottom="1.5bu">
                    <Text as="p" color="grey700" size="size4">
                        {__('Answer a few short questions to help us get to know you better.')}
                    </Text>
                </Box>
                <Button
                    size="large"
                    appearance="primary"
                    onClick={() => {
                        onboarderNav.goToStepByIndex(0);
                    }}
                >
                    {__('Get started')}
                </Button>
            </HeadingWrapper>
            <HeroContainer>
                <WandaIconWrapper>
                    <WandaIcon />
                </WandaIconWrapper>
                <AttributionWrapper>
                    <Attribution>
                        <FormattedMessage
                            message={__('illustrations by {designerName}')}
                            values={{ designerName: 'wildanya' }}
                        />
                    </Attribution>
                </AttributionWrapper>
            </HeroContainer>
        </Container>
    );
}
